import { Chip, Stack, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { MaterialLimitChip } from "components/common/chips/materialLimit";
import { FrontendMaterialLimit } from "components/common/forms/MaterialLimitsEditor";
import { useBaskets, useSearch } from "contexts/search/SearchContextProvider";
import { useTenantTranslation } from "hooks/formatters";
import { useMaterialLimits } from "hooks/search";
import { useIsAdmin, useTenant } from "hooks/settings";
import { loadedEndpoint, mapLoadedUnpack } from "models/loaded";
import React from "react";
import {
  BasketRead,
  MaterialLimit,
  useListMaterialConstraintClassesQuery,
} from "src/store/api/generatedApi";
import {
  createInitialMaterialLimit,
  getNewMaterialLimit,
  materialLimitToFrontend,
} from "src/utils/materialLimitConverters";
import {
  applyMaterialLimitDelete,
  applyMaterialLimitEdit,
  isMaterialLimitNullified,
  materialLimitKey,
} from "src/utils/materialLimitOverrides";
import { BasketMaterialLimitEditorDialog } from "./BasketMaterialLimitEditorDialog";

interface Props {
  chefGroupId: number;
  basket: BasketRead | null;
  materialLimits?: MaterialLimit[];
}

export const MaterialLimitsPanel = ({
  chefGroupId,
  basket,
  materialLimits: materialLimitOverridesProp,
}: Props) => {
  const {
    materialLimits: materialLimitsLoaded,
    setMaterialLimitOverrides,
    baseMaterialLimits,
  } = useMaterialLimits();
  const isAdmin = useIsAdmin();
  const { t } = useTenantTranslation();
  const baskets = useBaskets();
  const tenant = useTenant();
  const contextIds = mapLoadedUnpack(
    useSearch().context,
    ({ materialsId, materialPhysicsId }) => ({
      materialsId,
      materialPhysicsId,
    })
  );
  const materialConstraintClasses = loadedEndpoint(
    useListMaterialConstraintClassesQuery(
      contextIds === null
        ? skipToken
        : {
            tenantName: tenant,
            materialMetadataSetId: contextIds.materialsId,
            materialPhysicsSetId: contextIds.materialPhysicsId,
          }
    )
  );

  const baseKeys = React.useMemo(
    () =>
      new Set(
        (mapLoadedUnpack(baseMaterialLimits, (limits) => limits) ?? []).map(
          materialLimitKey
        )
      ),
    [baseMaterialLimits]
  );

  const filtered = React.useMemo(() => {
    const allMaterialLimits =
      materialLimitOverridesProp ??
      mapLoadedUnpack(materialLimitsLoaded, (materialLimit) => materialLimit) ??
      [];
    return allMaterialLimits.filter(
      (materialLimit) =>
        materialLimit.chef_group_id === chefGroupId &&
        !isMaterialLimitNullified(materialLimit) &&
        materialLimit.bounds.some((bound) =>
          basket === null
            ? bound.basket_id == null
            : bound.basket_id === basket.id
        )
    );
  }, [materialLimitOverridesProp, materialLimitsLoaded, chefGroupId, basket]);

  const [inEdit, setInEdit] = React.useState<null | {
    isNew: boolean;
    materialLimit: MaterialLimit;
  }>(null);

  const handleSubmit = React.useCallback(
    (
      originalMaterialLimit: MaterialLimit,
      frontendMaterialLimit: FrontendMaterialLimit
    ) => {
      const updated: MaterialLimit = getNewMaterialLimit(
        frontendMaterialLimit,
        chefGroupId
      );
      setMaterialLimitOverrides((current) =>
        applyMaterialLimitEdit(current, {
          original: originalMaterialLimit,
          updated,
          baseKeys,
        })
      );
    },
    [setMaterialLimitOverrides, chefGroupId, baseKeys]
  );

  const handleDelete = React.useCallback(
    (target: MaterialLimit) => {
      setMaterialLimitOverrides((current) =>
        applyMaterialLimitDelete(current, { target, baseKeys })
      );
    },
    [setMaterialLimitOverrides, baseKeys]
  );

  const handleAdd = React.useCallback(
    (frontendMaterialLimit: FrontendMaterialLimit) => {
      const newMaterialLimit: MaterialLimit = getNewMaterialLimit(
        frontendMaterialLimit,
        chefGroupId
      );
      setMaterialLimitOverrides((current) => [...current, newMaterialLimit]);
    },
    [setMaterialLimitOverrides, chefGroupId]
  );

  const isAddingNewLimit = inEdit?.isNew ?? false;

  return (
    <Stack gap={2}>
      <Stack gap={1}>
        <Typography>
          {basket === null
            ? t("mix")
            : `${t("basketNumber")} ${basket.basket_number}`}
        </Typography>
        <Stack
          flexDirection="row"
          flexWrap="wrap"
          data-testid={`material-limits-${
            basket === null ? "mix" : `basket-${basket.basket_number}`
          }`}
        >
          {filtered.map((materialLimit) => {
            const matchingBound = materialLimit.bounds.find((bound) =>
              basket === null
                ? bound.basket_id == null
                : bound.basket_id === basket.id
            );
            const key = `${materialLimit.name ?? ""}-${matchingBound?.basket_id ?? "mix"}-${matchingBound?.min_mass ?? ""}-${matchingBound?.max_mass ?? ""}`;
            return (
              <MaterialLimitChip
                key={key}
                minMass={matchingBound?.min_mass ?? null}
                maxMass={matchingBound?.max_mass ?? null}
                name={materialLimit.name ?? ""}
                onClick={() => {
                  setInEdit({
                    isNew: false,
                    materialLimit,
                  });
                }}
              />
            );
          })}
          {isAdmin && baskets.status === "success" ? (
            <Chip
              data-testid="add-material-limit"
              sx={{ mr: 1, mb: 1 }}
              label="+"
              size="small"
              color="primary"
              onClick={() => {
                setInEdit({
                  isNew: true,
                  materialLimit: {
                    name: "",
                    hardness: 1,
                    coefficients: [],
                    bounds: [],
                    chef_group_id: chefGroupId,
                  },
                });
              }}
            />
          ) : null}
        </Stack>
      </Stack>

      {inEdit ? (
        <BasketMaterialLimitEditorDialog
          doClose={() => {
            setInEdit(null);
          }}
          title={
            isAddingNewLimit ? t("createMaterialLimit") : t("editMaterialLimit")
          }
          materialLimit={
            baskets.status === "success"
              ? isAddingNewLimit
                ? createInitialMaterialLimit(baskets.data)
                : materialLimitToFrontend(inEdit.materialLimit, baskets.data)
              : createInitialMaterialLimit([])
          }
          materialConstraintClasses={materialConstraintClasses}
          onSubmit={(frontendMaterialLimit) => {
            if (isAddingNewLimit) {
              handleAdd(frontendMaterialLimit);
            } else {
              handleSubmit(inEdit.materialLimit, frontendMaterialLimit);
            }
          }}
          onDelete={
            isAddingNewLimit
              ? undefined
              : () => {
                  handleDelete(inEdit.materialLimit);
                }
          }
        />
      ) : null}
    </Stack>
  );
};
